import { CdkDrag, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ContentChild,
  DestroyRef,
  effect,
  ElementRef,
  EventEmitter,
  HostBinding,
  input,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  signal,
  SimpleChanges,
  TemplateRef,
  viewChild,
  ViewChild,
  ViewChildren,
  inject
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MAT_MOMENT_DATE_FORMATS,
  MomentDateAdapter
} from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpParams } from '@angular/common/http';
import { ConfirmationDialogComponent } from '@coin/shared/core/ui-storybook';
import { PlanType } from '@coin/shared/core/util-enums';
import { onResize, TinyHelpers } from '@coin/shared/core/util-helpers';
import { ConfirmationDialogConfirm, PaginatedResult, SimpleTableService } from '@coin/shared/core/util-models';
import moment, { isMoment } from 'moment';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, combineLatest, Observable, repeat, skip, Subscription } from 'rxjs';
import { debounceTime, finalize, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { TableToggleEvent } from '../../interfaces/table-toggle-event.interface';
import { SimpleTableGroupState } from './group-state.enum';
import { CdkScrollable, ScrollDispatcher } from '@angular/cdk/scrolling';

/* eslint-disable @typescript-eslint/no-explicit-any */
export interface SimpleTableHeader {
  title: string;
  width?: string;
  minWidth?: string;
  icon?: string;
  hide?: boolean;
  hideSorting?: boolean;
  hideFilter?: boolean;
  key?: string;
  fieldValuesKey?: string;
  checkAll?: boolean;
  matchItems?: Array<any>;
  special?: 'name' | 'label' | 'date' | 'role' | 'match' | 'number' | string;
  filterType?: 'date' | 'dropdown' | 'input' | 'custom' | 'customDropdown' | 'datePeriod';
  filterTooltip?: string;

  customFilterConfig?: SimpleTableCustomFilter;
  values?: any;
  async?: boolean;
  overlay?: boolean;
  overlayStart?: boolean;
  overlayEnd?: boolean;
  overlayContrast?: boolean;
  centered?: boolean;
  group?: string;
  id?: string;
}

export interface SimpleTableCustomFilter {
  filterClick?: (instance: SimpleTableComponent) => void;
  filterFunction?: (instance: SimpleTableComponent) => string;
}

@Component({
  selector: 'app-simple-table',
  templateUrl: './simple-table.component.html',
  styleUrls: ['./simple-table.component.scss'],
  providers: [
    { provide: DateAdapter, useClass: MomentDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } }
  ],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SimpleTableComponent implements OnInit, AfterViewInit, OnChanges {
  private dialog = inject(MatDialog);
  private scrollDispatcher = inject(ScrollDispatcher);
  private toast = inject(ToastrService);
  private destroyRef = inject(DestroyRef);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);

  readonly header = input<SimpleTableHeader[] | string[]>([]);
  readonly externalDataInput$ = input<Observable<any[]>>(undefined);
  readonly externalDataList = input<any[]>(undefined);
  readonly service = input<SimpleTableService>(undefined);
  readonly withSearch = input(false);
  readonly hidePagination = input(false);
  readonly serverside = input(false);
  readonly serverPaginated = input(false);
  readonly filterPrefix = input('');
  readonly sortingPrefix = input('');
  readonly saveFilterToUrl = input(false);
  readonly saveSortingToUrl = input(false);
  readonly defaultFilter = input('');
  readonly dragAndDrop = input(false);
  // TODO: Refactor to use v2 inputs instead of old inputs
  readonly hasV2DatePicker = input(false);
  readonly pageSize = input(50);
  lazyLoadFn = input<(page: number, search: string, key: string) => Observable<PaginatedResult<string>>>();
  readonly defaultInactiveGroups = input<string[]>(undefined);
  readonly dropdownDisplayFn = input<(arg0: string) => string>(undefined);
  readonly areAllRecordsSelected = input<boolean>(undefined);
  @Input() areAllItemsSelected: boolean;
  @Input() @HostBinding('class.horizontal-scrolling') horizontalScrolling: boolean;

  @Output() checkAllToggleChange = new EventEmitter<TableToggleEvent>();
  @Output() itemDrop = new EventEmitter();
  @Output() reload = new EventEmitter<void>();

  @ContentChild('columns') columnsRef: TemplateRef<unknown>;
  @ContentChild('buttons') buttonsRef: TemplateRef<unknown>;
  @ContentChild('noData') noDataRef: TemplateRef<unknown>;
  scrollable = viewChild(CdkScrollable);

  tableContentWrapperElementRef = viewChild<ElementRef<HTMLDivElement>>('tableContentWrapper');
  tableHeaderElementRef = viewChild<ElementRef<HTMLDivElement>>('tableHeader');
  @ViewChild('paginator', { static: false }) paginator: MatPaginator;
  @ViewChildren('row') rows: QueryList<ElementRef>;

  isContentScrollBarVisible$ = toObservable(this.tableContentWrapperElementRef).pipe(
    switchMap(elementRef => onResize(elementRef.nativeElement)),
    map(({ target }) => TinyHelpers.isVerticallyScrollable(target as HTMLElement))
  );

  private readonly minColumnWidth = '38px';

  public inactiveGroups: string[] = [];
  public inactiveColumns$ = new BehaviorSubject<SimpleTableGroupState[]>([]);

  public showFilter = '';

  private _data = signal([]);
  public set data(data: any[]) {
    this._data.set(data);
  }

  public get data(): any[] {
    return this._data();
  }

  protected originalData: any[] = [];
  public templateColumns: string;

  public loading = signal(false);

  private _length = signal(50);
  public get length() {
    return this._length();
  }
  public set length(val: number) {
    this._length.set(val);
  }

  public pageSizeOptions: number[] = [10, 25, 50, 100, 200];

  private pageChanged$ = new BehaviorSubject(this.getInitPageEvent());
  public searchChanged$ = new BehaviorSubject('');
  public filterChanged$ = new BehaviorSubject('');
  public sortingChanged$ = new BehaviorSubject('');
  protected reloadTrigger$ = new BehaviorSubject<unknown>(null);

  public sortedItemsBy: Record<string, string> = {};
  public filterVariables: Record<string, any> = {};

  public get headerObjects(): SimpleTableHeader[] {
    const header = this.header();
    return this.isStringArray(header) ? header?.map(item => ({ title: item })) : header;
  }

  public get dataAvailable(): number {
    return this.data?.length;
  }

  public get count(): number {
    return this.length;
  }

  public isGroupStart(item: SimpleTableHeader, i: number): boolean {
    return this.headerObjects[i - 1]?.group !== item.group && this.headerObjects[i + 1]?.group === item.group;
  }

  public isGroupEnd(item: SimpleTableHeader, i: number): boolean {
    return this.headerObjects[i - 1]?.group === item.group && this.headerObjects[i + 1]?.group !== item.group;
  }

  public isItemInactive(group: string): boolean {
    return this.inactiveGroups.includes(group);
  }

  constructor() {
    effect(() => {
      const scrollableInstance = this.scrollable();
      if (scrollableInstance) {
        this.scrollDispatcher.register(scrollableInstance);
      }
    });
  }

  ngOnInit(): void {
    this.setTemplateColumns();

    const externalDataInput$ = this.externalDataInput$();
    if (externalDataInput$) {
      externalDataInput$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(data => {
        this.data = data;
        this.originalData = structuredClone(this.data);
      });
    }

    const externalDataList = this.externalDataList();
    if (externalDataList) {
      this.data = externalDataList;
      this.originalData = structuredClone(this.data);
    }

    if (this.defaultInactiveGroups()) {
      this.setDefaultInactiveGroups();
    }
  }

  private setTemplateColumns(): void {
    const header = this.header();
    this.templateColumns = this.isStringArray(header)
      ? `repeat(${header.length}, minmax(${this.minColumnWidth}, 1fr))`
      : header
          ?.map(item =>
            item.minWidth ? this.getMinWidthGridColumSize(item.minWidth) : this.getGridColumnSize(item.width)
          )
          .join(' ');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.service) {
      setTimeout(() => {
        this.pageChanged$.next(this.getInitPageEvent());
      });
    }

    if (changes.externalDataList) {
      this.data = this.externalDataList();
      this.originalData = structuredClone(this.data);
    }
  }

  ngAfterViewInit(): void {
    const debouncedSearchWithPaginatorReset$ = this.searchChanged$.pipe(
      debounceTime(400),
      tap(() => this.resetPagination())
    );

    if (this.service()) {
      combineLatest([
        this.pageChanged$,
        debouncedSearchWithPaginatorReset$,
        this.filterChanged$,
        this.sortingChanged$,
        this.reloadTrigger$
      ])
        .pipe(
          tap(() => this.loading.set(true)),
          debounceTime(500),
          switchMap(([pageSettings, search, filter, sortText]) =>
            this.service().queryPagedAndFilteredData(
              pageSettings.pageIndex,
              pageSettings.pageSize,
              search,
              `${filter}${this.defaultFilter()}`,
              sortText
            )
          ),
          withLatestFrom(this.pageChanged$),
          finalize(() => this.loading.set(false)),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(([result, pageSettings]) => {
          this.loading.set(false);

          const serverside = this.serverside();
          if (!serverside && !this.serverPaginated()) {
            TinyHelpers.assert(Array.isArray(result));
            this.setDataAndUpdatePaginator(result, pageSettings.pageSize, pageSettings.pageIndex);
            if (this.service()) {
              this.applyFilter();
            }
          } else {
            TinyHelpers.assert(!Array.isArray(result));
            this.data = result?.content;
            this.originalData = this.data ? structuredClone(this.data) : null;
            this.length = result?.total;
            this.toggleVisibleColumns();
          }
          if (!serverside) {
            this.applyFilterAndSortingFromUrl();
          }
          this.reload.emit();
        });
    }

    if (this.serverside()) {
      this.applyFilterAndSortingFromUrl();
    }
  }

  private setDefaultInactiveGroups(): void {
    this.inactiveGroups = [...this.defaultInactiveGroups()];
    this.toggleVisibleColumns();
  }

  private isStringArray(header: SimpleTableHeader[] | string[]): header is string[] {
    return (header as string[])?.every(item => typeof item === 'string');
  }

  public toggleInactive(group: string): void {
    if (this.inactiveGroups.includes(group)) {
      this.inactiveGroups = this.inactiveGroups.filter(item => item !== group);
    } else {
      this.inactiveGroups.push(group);
    }

    this.toggleVisibleColumns();
  }

  public highlightRow(item: any): void {
    const rowElement = this.rows.find(row => row.nativeElement.id === item.id)?.nativeElement;

    if (!rowElement) return;

    rowElement.scrollIntoView({ behavior: 'smooth' });
    if (rowElement.classList.contains('highlight')) rowElement.classList.remove('highlight');
    rowElement.classList.add('highlight');

    setTimeout(() => {
      rowElement.classList.remove('highlight');
    }, 4000);
  }

  private toggleVisibleColumns(): void {
    const headerStates: SimpleTableGroupState[] = this.getHeaderGroupStates();
    this.inactiveColumns$.next(headerStates);
    this.templateColumns = (this.header() as SimpleTableHeader[])
      ?.map((item, index) => {
        switch (headerStates[index]) {
          case SimpleTableGroupState.Visible:
            return item.minWidth ? this.getMinWidthGridColumSize(item.minWidth) : this.getGridColumnSize(item.width);
          case SimpleTableGroupState.Inactive:
            return '120px';
          case SimpleTableGroupState.Hidden:
          default:
            return '';
        }
      })
      .join(' ');
    this.prepareContentForScrolling();
  }

  private prepareContentForScrolling(): void {
    setTimeout(() => {
      const scrollBarWidth = TinyHelpers.getScrollBarWidth();

      const { nativeElement: tableHeaderElement } = this.tableHeaderElementRef();
      const { nativeElement: tableContentWrapperElementRef } = this.tableContentWrapperElementRef();

      if (!tableHeaderElement || !tableContentWrapperElementRef) {
        return;
      }

      const isContentVerticallyScrollable = TinyHelpers.isVerticallyScrollable(tableContentWrapperElementRef);
      const isContentHorizontalScrollable =
        TinyHelpers.isHorizontallyScrollable(tableContentWrapperElementRef) ||
        TinyHelpers.isHorizontallyScrollable(tableHeaderElement);

      if (isContentHorizontalScrollable) {
        tableContentWrapperElementRef.onscroll = (event: WheelEvent) => {
          const { scrollLeft } = event.target as HTMLElement;
          tableHeaderElement.style.setProperty('left', `-${scrollLeft}px`);
          tableHeaderElement.style.setProperty('position', `absolute`);

          const headerHeight = tableHeaderElement.getBoundingClientRect().height.toString();
          tableContentWrapperElementRef.style.setProperty('margin-top', `${headerHeight}px`);
        };
      }

      if (isContentVerticallyScrollable) {
        // Account for the scroll bar width on the right side of the header
        tableHeaderElement.style.setProperty('padding-right', `${scrollBarWidth}px`);
      } else {
        tableHeaderElement.style.setProperty('padding-right', `0`);
      }
    });
  }

  private getHeaderGroupStates(): SimpleTableGroupState[] {
    return (this.header() as SimpleTableHeader[])?.map((item, index) => {
      if (this.isItemInactive(item.group)) {
        return this.isGroupStart(item, index) ? SimpleTableGroupState.Inactive : SimpleTableGroupState.Hidden;
      }
      return SimpleTableGroupState.Visible;
    });
  }

  public checkAllToggle(e: boolean): void {
    if (this.areAllRecordsSelected() || this.areAllItemsSelected) {
      this.areAllItemsSelected = false;
    }
    this.checkAllToggleChange.emit({
      checked: e,
      items: this.data
    });
  }

  public sortBy(item: SimpleTableHeader, sortDirection: 'asc' | 'desc'): void {
    this.setSorting(item, sortDirection);
    const sortingText = this.getSortingParamString();

    if (!this.serverside()) {
      if (this.sortedItemsBy.type) {
        this.data = this.sortByKey(this.data, this.sortedItemsBy.type, item.special);

        if (this.sortedItemsBy.direction === 'desc') {
          this.data = this.data.reverse();
        }
      } else {
        this.data = this.sortByKey(this.data, 'id');
      }
    } else {
      this.sortingChanged$.next(sortingText);
    }

    this.setSortingUrlParams(sortingText);
  }

  private getSortingParamString(): string {
    return Object.keys(this.sortedItemsBy)?.length
      ? `&${this.sortingPrefix()}Property=${this.sortedItemsBy.type}&${this.sortingPrefix()}OrderBy=${this.sortedItemsBy.direction}`
      : '';
  }

  private setSorting(item: SimpleTableHeader, sortDirection: 'asc' | 'desc'): void {
    if (this.sortedItemsBy.type === item.key && this.sortedItemsBy.direction === sortDirection) {
      this.sortedItemsBy = {};
      return;
    }

    this.sortedItemsBy = {
      type: item.key,
      direction: sortDirection
    };
  }

  public clearFilter(): void {
    this.filterVariables = {};
    this.applyFilter();
  }

  public clearFilterByKey(key: string): void {
    Object.keys(this.filterVariables).forEach(k => {
      if (key === k || k.startsWith(`${key}---`)) {
        this.filterVariables[k] = null;
      }
    });

    this.applyFilter();
  }

  private clearSorting(): void {
    this.sortedItemsBy = {};
    this.sortingChanged$.next('');
  }

  public resetPagination(): void {
    if (!this.hidePagination() && this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.pageChanged$.next(this.getInitPageEvent());
  }

  public sortByKey(array: unknown[], key: string, special = ''): unknown[] {
    switch (special) {
      case 'name':
        return array.sort(this.sortByName(key));
      case 'othername':
        return array.sort(this.sortByName(key, true));
      case 'number':
        return array.sort(this.sortByNumber(key));
      case 'label':
        return array.sort(this.sortByLabel(key));
      case 'date':
        return array.sort(this.sortByDate(key));
      case 'role':
        return array.sort(this.sortByRole(key));
      case 'match':
        return array.sort(this.sortByMatch(key));
      default:
        return special.startsWith('path')
          ? array.sort(this.sortByPath(key, special))
          : array.sort(this.defaultSort(key));
    }
  }

  private sortByName(key: string, other = false): (a: any, b: any) => number {
    if (other) {
      return (a, b) => {
        const x = a[key].firstname?.toLowerCase() + a[key].lastname?.toLowerCase();
        const y = b[key].firstname?.toLowerCase() + b[key].lastname?.toLowerCase();
        return this.basicSort(x, y);
      };
    }
    return (a, b) => {
      const x = a[key].name.firstname.toLowerCase() + a[key].name.lastname.toLowerCase();
      const y = b[key].name.firstname.toLowerCase() + b[key].name.lastname.toLowerCase();
      return this.basicSort(x, y);
    };
  }

  private sortByNumber(key: string): (a: any, b: any) => number {
    return (a, b) => a[key] - b[key];
  }

  private sortByMatch(key: string): (a: any, b: any) => number {
    const { matchItems } = (this.header() as SimpleTableHeader[]).find(itm => itm.key === key);
    return (a, b) => {
      const x = matchItems[a[key]];
      const y = matchItems[b[key]];
      return this.basicSort(x, y);
    };
  }

  private sortByLabel(key: string): (a: any, b: any) => number {
    return (a, b) => {
      const x = this.getAllArrayItemsAsString(a[key], 'name');
      const y = this.getAllArrayItemsAsString(b[key], 'name');
      return this.basicSort(x, y);
    };
  }

  private sortByDate(key: string): (a: any, b: any) => number {
    return (a, b) => {
      let x = new Date(a[key]).getTime();
      let y = new Date(b[key]).getTime();
      x = isNaN(x) ? 0 : x;
      y = isNaN(y) ? 0 : y;
      return this.basicSort(x, y);
    };
  }

  private sortByRole(key: string): (a: any, b: any) => number {
    return (a, b) => {
      const aSort = a[key].sort(this.arraySortByKey('name'));
      const bSort = b[key].sort(this.arraySortByKey('name'));
      const x = this.getAllArrayItemsAsString(aSort, 'name');
      const y = this.getAllArrayItemsAsString(bSort, 'name');
      return this.basicSort(x, y);
    };
  }

  private arraySortByKey(key: string): (a: any, b: any) => number {
    return (a, b) => this.basicSort(a[key], b[key]);
  }

  private sortByPath(key: string, special: string): (a: any, b: any) => number {
    return (a, b) => {
      let x = a;
      let y = b;
      const path = special.split('|').slice(1);
      for (const itm of path) {
        x = x[itm] || '';
        y = y[itm] || '';
      }
      return this.basicSort(x, y);
    };
  }

  private defaultSort(key: string): (a: any, b: any) => number {
    return (a, b) => this.basicSort(a[key]?.toLowerCase(), b[key]?.toLowerCase());
  }

  private basicSort(x: string | number, y: string | number): number {
    if (x < y) return -1;
    if (x > y) return 1;
    return 0;
  }

  private getAllArrayItemsAsArray(array: any[], key: string): unknown[] {
    return array.map(itm => itm[key] || '');
  }

  private getAllArrayItemsAsString(array: any[], key: string): string {
    try {
      return array.reduce((acc, itm) => acc + itm[key] || '', '');
    } catch (/* eslint-disable-line @typescript-eslint/no-unused-vars */ error) {
      return '';
    }
  }

  private getGridColumnSize(width: string): string {
    if (!width) {
      return `minmax(${this.minColumnWidth}, 1fr)`;
    }
    if (width.includes('px')) {
      return width;
    }

    const frSuffix = width.includes('fr') ? '' : 'fr';
    return `minmax(${this.minColumnWidth}, ${width}${frSuffix})`;
  }

  private getMinWidthGridColumSize(minWidth: string): string {
    return `minmax(${minWidth}, 1fr)`;
  }

  private setDataAndUpdatePaginator(data: any[], pageSize: number, pageIndex: number): void {
    if (data) {
      this.data = data;
      this.originalData = structuredClone(this.data);

      if (data.length < pageSize) {
        this.length = pageSize * pageIndex + data.length;
      } else if (pageSize * (pageIndex + 2) > this.length) {
        this.length = pageSize * (pageIndex + 2);
      }
    }
  }

  public onSearchChange(text: string): void {
    this.searchChanged$.next(text);
  }

  public triggerReload(): void {
    this.reloadTrigger$.next(null);
  }

  public pageChange(event: PageEvent): void {
    this.pageChanged$.next(event);
  }

  private getInitPageEvent(): PageEvent {
    return { pageIndex: 0, pageSize: this.pageSize() } as PageEvent;
  }

  public trackByItemFn(index: number, item: Record<string, string>): string | number {
    return item?.id || index;
  }

  // FILTER
  public showFilterOptions(header: SimpleTableHeader, forceShow = false): void {
    if (!header.filterType) {
      return;
    }

    if (header.filterType === 'custom') {
      this.showFilter = '';
      header.customFilterConfig?.filterClick(this);
      this.applyFilter();
    } else if (forceShow) {
      this.showFilter = header.key;
    } else {
      this.showFilter = this.showFilter === header.key ? '' : header.key;
    }
  }

  public getDropdownVals = (array: SimpleTableHeader, originalData: any[]): string[] => {
    if (this.serverside()) {
      if (!array?.values) {
        console.error('No values defined for column: ', array?.key);
        return [];
      }

      return array.values;
    }

    const { special, key } = array;

    let dropvals = [];
    switch (special) {
      case 'name':
        dropvals = originalData.map(itm => `${itm[key]?.name?.firstname} ${itm[key]?.name?.lastname}`);
        break;
      case 'othername':
        dropvals = originalData.map(itm => {
          const first = itm[key]?.firstname || '';
          const last = itm[key]?.lastname || '';
          const fullName = `${first} ${last}`.trim();
          return fullName || '__unknown__';
        });
        break;
      case 'match':
        dropvals = originalData.map(itm => array.matchItems[itm[key]]);
        break;
      case 'label':
      case 'role':
        dropvals = originalData.reduce((acc, itm) => [...acc, ...this.getAllArrayItemsAsArray(itm[key], 'name')], []);
        break;
      case 'path':
        dropvals = originalData.map(itm => {
          let val = itm[key];
          const path = special.split('|').slice(1, special.split('|').length);
          for (const item of path) {
            val = val[item] ? val[item] : '';
          }
          return val;
        });
        break;
      default:
        dropvals = originalData.map(itm => itm[key]);
        break;
    }

    return [...new Set(dropvals)]; // only unique values
  };

  public applyFilter(): void {
    const filterText = this.getFilterParamString();

    if (this.serverside()) {
      this.serversideFiltering(filterText);
    } else {
      this.localFiltering();
    }

    this.setFilterUrlParams(filterText);
  }

  private serversideFiltering(filterParamString: string): void {
    this.resetPagination();
    this.filterChanged$.next(filterParamString);
  }

  private getFilterParamString(): string {
    let filterTexts = [];

    for (const key in this.filterVariables) {
      const headerItem = (this.header() as SimpleTableHeader[]).find(
        item => item.key === key || item.key === key.split('---')[0]
      );
      const value = this.filterVariables[key];

      if (!this.filterValid(value, headerItem)) {
        continue;
      }

      switch (headerItem.filterType) {
        case 'input':
          filterTexts.push(`${this.filterPrefix()}${headerItem.key}=${encodeURIComponent(value as string | number)}`);
          break;
        case 'date':
          filterTexts.push(`${this.filterPrefix()}${key}=${encodeURIComponent((value as Date).toISOString())}`);
          break;
        case 'dropdown':
          for (const dropdownValue of value as string[]) {
            filterTexts.push(`${this.filterPrefix()}${headerItem.key}=${encodeURIComponent(dropdownValue)}`);
          }
          break;
        case 'datePeriod':
          filterTexts.push(
            `${this.filterPrefix()}${key.split('---').join('')}=${encodeURIComponent(value.toISOString())}`
          );
          break;
        case 'customDropdown':
        case 'custom':
          filterTexts.push(headerItem.customFilterConfig?.filterFunction(this));
          break;
      }
    }

    filterTexts = filterTexts?.filter(text => !!text);
    return `${filterTexts.length ? '&' : ''}${filterTexts?.join('&')}`;
  }

  private filterValid(value: any, header: SimpleTableHeader): boolean {
    if (header.filterType === 'datePeriod' && typeof value === 'object' && value !== null) {
      // Iterate over object keys to find any matching ---From or ---To patterns
      for (const key of Object.keys(value)) {
        // Check if key ends with ---From or ---To and has a valid moment date
        if ((key.endsWith('---From') || key.endsWith('---To')) && moment.isMoment(value[key])) {
          return true; // Valid if any key matches and has a valid moment date
        }
      }
    }

    return (
      (typeof value === 'string' ? !!value : value !== undefined && value !== null) || header.filterType === 'custom'
    );
  }

  private localFiltering(): void {
    let newData = structuredClone(this.originalData);
    const hObjs = this.headerObjects;

    for (const keyVal in this.filterVariables) {
      if (!this.filterVariables.hasOwnProperty(keyVal)) {
        continue;
      }

      const elem = this.filterVariables[keyVal];
      const header = hObjs.find(itm => itm.key === keyVal.split('---')[0]);

      if (!header) continue;

      const { special, key } = header;

      if (typeof elem === 'string' && elem) {
        newData = this.filterByText(newData, special, elem, keyVal);
      } else if (elem && typeof elem === 'object' && elem.length === 1 && typeof elem[0] === 'string') {
        newData = this.filterByObject(newData, special, elem, keyVal, key);
      } else if (elem && typeof elem === 'object' && (elem.length > 0 || special === 'date')) {
        newData = this.filterByObject(newData, special, elem, keyVal, key);
      }
    }

    this.data = newData;
  }

  private filterByText(data: any[], special: string, elem: string, keyVal: string): unknown[] {
    return special && special.startsWith('path')
      ? data.filter(itm => this.getDeepestMatch(itm, special)?.toLowerCase().includes(elem?.toLowerCase()))
      : data.filter(itm => {
          const filterValues = elem ? elem.split(',').map(value => value.trim().toLowerCase()) : [];
          const itmValue = itm[keyVal]?.toString()?.toLowerCase();
          return filterValues.some(fv => itmValue?.includes(fv));
        });
  }

  private getDeepestMatch(obj: Record<string, unknown>, path: string): string {
    const attributes = path.split('|').slice(1);
    return attributes.reduce((acc, key) => (acc ? acc[key] : obj[key]) || '', null);
  }

  private filterByObject(data: any[], special: string, elem: any[], keyVal: string, key: string): unknown[] {
    switch (special) {
      case 'name':
        return data.filter(itm => elem.includes(`${itm[keyVal].name.firstname} ${itm[keyVal].name.lastname}`));
      case 'othername':
        return data.filter(itm => {
          const first = itm[key]?.firstname || '';
          const last = itm[key]?.lastname || '';
          const fullName = `${first} ${last}`.trim();
          const valueToCompare = fullName || '__unknown__';
          return elem.includes(valueToCompare);
        });
      case 'label':
      case 'role':
        return data.filter(itm => elem.some(item => this.getAllArrayItemsAsArray(itm[keyVal], 'name').includes(item)));
      case 'date': {
        const baseKey = keyVal.split('---')[0];
        const direction = keyVal.split('---')[1];

        return data.filter(itm => {
          const rawValue = itm[baseKey];
          let itemDate: moment.Moment;
          const filterDate = moment(elem);

          if (moment.isMoment(rawValue)) {
            itemDate = rawValue;
          } else if (!isNaN(Number(rawValue))) {
            itemDate = moment(Number(rawValue));
          } else {
            itemDate = moment(rawValue, moment.ISO_8601, true);
          }

          if (!itemDate.isValid() || !filterDate.isValid()) return false;

          return direction === 'From'
            ? itemDate.isSameOrAfter(filterDate.startOf('day'))
            : itemDate.isSameOrBefore(filterDate.endOf('day'));
        });
      }
      case 'match': {
        const { matchItems } = (this.header() as SimpleTableHeader[]).find(itm => itm.key === keyVal);
        const selected = elem.map(itm => matchItems.findIndex(item => item === itm));
        return data.filter(itm => selected.indexOf(itm[keyVal]) > -1);
      }
      default:
        return data.filter(itm => elem.some(e => e.split(',').includes(itm[keyVal])));
    }
  }

  public async onDrop(event: CdkDragDrop<unknown[]>): Promise<Subscription> {
    const filter = this.filterChanged$.value;
    const sorting = this.sortingChanged$.value;
    const page = this.pageChanged$.value;
    const userIsAllowedToDrag = !filter && !sorting && !page.pageIndex;
    const item = this.data[event.previousIndex];

    if (!userIsAllowedToDrag) {
      return this.dialog
        .open(ConfirmationDialogComponent, {
          disableClose: true,
          data: {
            headline: 'general.info',
            msg: `general.drag-not-allowed-info-msg`,
            cancelMsg: 'general.go-back',
            confirmMsg: 'general.reset',
            translate: true
          }
        })
        .afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((confirm?: ConfirmationDialogConfirm) => {
          if (!confirm) {
            return;
          }

          this.clearFilter();
          this.clearSorting();
          this.resetPagination();

          this.toast.info('Table filters and sorting resetted.');
        });
    }

    if (event.previousIndex === event.currentIndex) {
      return undefined;
    }

    moveItemInArray(this.data, event.previousIndex, event.currentIndex);
    this.itemDrop.emit(item);
    return undefined;
  }

  public sortPredicate(index: number, item: CdkDrag): boolean {
    const newPositionElement = this.data[index];
    const indexOfDefaultRow = this.data?.findIndex(item => item.type === PlanType.Default);
    const canMove = item?.data?.isSeparator || !newPositionElement?.isDragDisabled;

    if (indexOfDefaultRow !== -1) {
      return canMove && index <= indexOfDefaultRow;
    }

    return canMove;
  }

  filterSet(): boolean {
    for (const key in this.filterVariables) {
      if (!this.filterVariables.hasOwnProperty(key)) {
        continue;
      }

      const elem = this.filterVariables[key];
      // eslint-disable-next-line no-constant-condition
      if (typeof elem) {
        return true;
      }
    }
    return false;
  }

  public filterActive(key: string): boolean {
    // Check directly if the base key is active
    if (
      this.filterVariables.hasOwnProperty(key) &&
      (isMoment(this.filterVariables[key]) || this.filterVariables[key]?.length !== undefined)
    ) {
      return true;
    }

    // Check for any suffixes
    return Object.keys(this.filterVariables).some(subKey => {
      const isSubKeyActive = subKey === key || subKey.startsWith(`${key}---`);
      return (
        isSubKeyActive &&
        (isMoment(this.filterVariables[subKey]) ||
          (typeof this.filterVariables[subKey] === 'string' && this.filterVariables[subKey]?.length) || // For string types
          (Array.isArray(this.filterVariables[subKey]) && this.filterVariables[subKey].length) || // For arrays
          // For objects
          (typeof this.filterVariables[subKey] === 'object' && this.filterVariables[subKey] !== null))
      );
    });
  }

  private applyFilterAndSortingFromUrl(): void {
    const { queryParams } = this.activatedRoute.snapshot;
    const headerKeys = (this.header() as SimpleTableHeader[])?.map(item => item.key) ?? [];

    if (this.saveFilterToUrl()) {
      const filterParams = Object.keys(queryParams).filter(key => key.toString().startsWith(this.filterPrefix()));

      filterParams.forEach(key => {
        const keyWithoutPrefix = key.replace(this.filterPrefix(), '');

        if (headerKeys.some(e => e === keyWithoutPrefix || keyWithoutPrefix.startsWith(`${e}---`))) {
          const header = (this.header() as SimpleTableHeader[])?.find(
            item => item.key === keyWithoutPrefix || keyWithoutPrefix.startsWith(`${item.key}---`)
          );

          switch (header.filterType) {
            case 'date':
              this.filterVariables[keyWithoutPrefix] = moment(queryParams[key].toString());
              break;
            case 'dropdown':
              this.filterVariables[keyWithoutPrefix] = queryParams[key].toString().split(',');
              break;
            default:
              this.filterVariables[keyWithoutPrefix] = queryParams[key];
              break;
          }
          this.showFilterOptions(header, true);
        }
      });

      this.applyFilter();
    }

    if (this.saveSortingToUrl()) {
      const sortByProperty = queryParams[`${this.sortingPrefix()}Property`];
      const sortDirection = queryParams[`${this.sortingPrefix()}OrderBy`];

      const header = (this.header() as SimpleTableHeader[])?.find(item => item.key === sortByProperty);

      if (header) this.sortBy(header, sortDirection);
    }
  }

  private setFilterUrlParams(filterText: string): void {
    if (!this.saveFilterToUrl()) return;

    this.setUrlParamsFromParamString(filterText, this.filterPrefix());
  }

  private setSortingUrlParams(sortingText: string): void {
    if (!this.saveSortingToUrl()) return;

    this.setUrlParamsFromParamString(sortingText, this.sortingPrefix());
  }

  private setUrlParamsFromParamString(paramString: string, paramPrefix: string) {
    const params = new HttpParams({ fromString: paramString });
    const queryParams = { ...this.activatedRoute.snapshot.queryParams };

    const previousParamKeys = Object.keys(queryParams).filter(key => key.startsWith(paramPrefix));
    previousParamKeys.forEach(key => delete queryParams[key]);

    params
      .keys()
      .filter(key => !!key)
      .forEach(key => {
        queryParams[key] = params.getAll(key);
      });

    this.router.navigate([], { queryParams });
  }

  reloadableLazyLoadFn = computed(() => {
    const lazyLoadFn = this.lazyLoadFn();
    return (page: number, search: string, key: string) =>
      lazyLoadFn?.(page, search, key)?.pipe(repeat({ delay: () => this.reloadTrigger$.pipe(skip(1)) }));
  });
  createPatchedLazyLoadFn = (key: string): ((search: string) => Observable<string[]>) => {
    const reloadableLazyLoadFn = this.reloadableLazyLoadFn();
    return search => reloadableLazyLoadFn?.(0, search, key)?.pipe(map(result => result?.content));
  };
}
